import * as React from 'react';
import { getTranslator, Translator } from '../Translator';
import { Link, useNavigate, useRoutes } from 'react-router-dom';
import { allRoutes } from './../routes';
import { EmailLoginForm } from './EmailLoginForm';
import { DocumentTitle } from './DocumentTitle';
import { QuestionMark } from './ui/QuestionMark';
import { ContactInstitution } from './ContactInstitution';
import { AccountState, ElectionState, SurfConextFormArgs } from '../_generatedTypes.g';
import { useDispatch } from 'react-redux';
import { logout } from '../loginActionCreators';
import { isAnyCandidateElectionInCandidacyStatus } from '../selectors';
import { languageChanged } from '../features/language/languageSlice';
import { useAppSelector } from '../hooks';

const forceEmailLogin = () => window.location.hash === '#emaillogin';

const LanguageSwitch = () => {
    const dispatch = useDispatch();
    const justDutch = useAppSelector(state => state.clientState.JustDutch);
    const language = useAppSelector(state => state.language);

    const t = getTranslator(language);

    return justDutch ? <></> : t.translate({
        'EN': <button type="button" onClick={() => { dispatch(languageChanged('NL')); }}>Nederlands</button>,
        'NL': <button type="button" onClick={() => { dispatch(languageChanged('EN')); }}>English</button>,
    });
}

const ConextLogin = (t: Translator, conextFormArgs: SurfConextFormArgs, adminRedirectUrl: string | null) =>
    <div>
        <DocumentTitle title={t['Welcome to the elections!']} />
        <p className="login-welcome-title">{t['Welcome to the elections!']}</p>
        <p className="login-use-button">{t['Use the button below to log in.']}</p>
        <p className={t.languageCode === "NL" ? 'login-warning-NL' : 'login-warning-EN'}></p>
        <form method="POST" action={conextFormArgs.Action}>
            <input type="hidden" name="SAMLRequest" value={conextFormArgs.SAMLRequest} />
            <p>
                <button type="submit" className="button--important">{t['Log in']}</button>
            </p>
        </form>
        {adminRedirectUrl ? <p><a href={adminRedirectUrl}>{t['Administrators log in here']}</a></p> : null}
        <ContactInstitution title={'If you are unable to log in, please contact us via'} />
    </div>;

const State = (elections: ElectionState[], stateType: 'LOADING_STATE' | 'LOGGED_OUT_STATE' | 'LOGGED_IN_STATE' | 'PANIC_STATE', t: Translator, adminRedirectUrl: string | null, surfConextFormArgs: SurfConextFormArgs | null, account: AccountState) => {
    const overrideEditCandidateRouteEnabled = isAnyCandidateElectionInCandidacyStatus(elections);

    const routes = useRoutes(allRoutes(account, overrideEditCandidateRouteEnabled));

    if (stateType === 'LOADING_STATE') {
        return <div>{t['Loading...']}</div>;
    } else if (stateType === 'LOGGED_IN_STATE') {
        return routes;
    } else if (stateType === 'LOGGED_OUT_STATE') {
        return forceEmailLogin()
            ? <EmailLoginForm />
            : surfConextFormArgs
                ? ConextLogin(t, surfConextFormArgs, adminRedirectUrl)
                : <EmailLoginForm />;
    }
    return <div>{t.translate({
        'NL': <p>Er ging iets mis; <a href="#" onClick={() => window.location.reload()}>herlaad de pagina</a> en probeer uw laatse actie opnieuw.</p>,
        'EN': <p>Something went wrong; <a href="#" onClick={() => window.location.reload()}>reload the page</a> and try your last action again.</p>,
    })}</div>;
};

export const Layout = () => {
    const language = useAppSelector(state => state.language);
    const token = useAppSelector(state => state.clientState.RequestVerificationToken);
    const dispatch = useDispatch();
    const t = getTranslator(language);
    const elections = useAppSelector(state => state.elections);

    const stateType = useAppSelector(state => state.clientState.Type);
    const adminRedirect = useAppSelector(state => state.clientState.AdminRedirectUrl);
    const surfConextFormArgs = useAppSelector(state => state.clientState.SurfConextFormArgs);
    const account = useAppSelector(state => state.clientState.Account);
    const navigate = useNavigate();

    return <div>
        <QuestionMark>{t['Use the links below the institution logo to switch languages and to log out']}</QuestionMark>
        <Link to="/">
            <div className="logo__background">
                <div className={'logoheader institutionlogo__0 ' + (t.languageCode === "NL" ? 'institutionlogo__0--NL' : 'institutionlogo__0--EN')} />
            </div>
        </Link>
        <div className="toolbar__background">
            <nav className="toolbar">
                <LanguageSwitch />
                {stateType === 'LOGGED_IN_STATE' ? [
                    <button key="logout" type="button" onClick={() => logout(navigate, token, dispatch)}>{t['Logout']}</button>,
                    <span key="username" className="displayname">({account!.UserName})</span>
                ] : null}
            </nav>
        </div>
        <div className="content">
            {State(elections, stateType, t, adminRedirect, surfConextFormArgs, account)}
        </div>
        <div className="brand">
            <span className="brand-text">Powered by <a href="https://progressverkiezingen.nl" target="_blank" rel="noreferrer noopener">ProgressOnderwijs</a></span>
        </div>
        <div className="footer"> </div>
    </div>;
};
